import {
  DEFAULT_FILENAME,
  EXPORT_DATA_TYPES,
  MIME_TYPES,
  VERSIONS,
  getExportSource,
  isDrawinkDataType,
} from "@/lib/common";

import type { DrawinkElement } from "@/lib/elements/types";

import { cleanAppStateForExport, clearAppStateForDatabase } from "../appState";

import { isImageFileHandle, loadFromBlob } from "./blob";
import { fileOpen, fileSave } from "./filesystem";

import type { AppState, BinaryFiles, LibraryItems } from "../types";
import type {
  ExportedDataState,
  ExportedLibraryData,
  ImportedDataState,
  ImportedLibraryData,
} from "./types";

/**
 * Strips out files which are only referenced by deleted elements
 */
const filterOutDeletedFiles = (elements: readonly DrawinkElement[], files: BinaryFiles) => {
  const nextFiles: BinaryFiles = {};
  for (const element of elements) {
    if (!element.isDeleted && "fileId" in element && element.fileId && files[element.fileId]) {
      nextFiles[element.fileId] = files[element.fileId];
    }
  }
  return nextFiles;
};

export const serializeAsJSON = (
  elements: readonly DrawinkElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
  type: "local" | "database",
): string => {
  const data: ExportedDataState = {
    type: EXPORT_DATA_TYPES.drawink,
    version: VERSIONS.drawink,
    source: getExportSource(),
    elements,
    appState:
      type === "local" ? cleanAppStateForExport(appState) : clearAppStateForDatabase(appState),
    files:
      type === "local"
        ? filterOutDeletedFiles(elements, files)
        : // will be stripped from JSON
          undefined,
  };

  return JSON.stringify(data, null, 2);
};

export const saveAsJSON = async (
  elements: readonly DrawinkElement[],
  appState: AppState,
  files: BinaryFiles,
  /** filename */
  name: string = appState.name || DEFAULT_FILENAME,
) => {
  const serialized = serializeAsJSON(elements, appState, files, "local");
  const blob = new Blob([serialized], {
    type: MIME_TYPES.drawink,
  });

  const fileHandle = await fileSave(blob, {
    name,
    extension: "drawink",
    description: "Drawink file",
    fileHandle: isImageFileHandle(appState.fileHandle) ? null : appState.fileHandle,
  });
  return { fileHandle };
};

export const loadFromJSON = async (
  localAppState: AppState,
  localElements: readonly DrawinkElement[] | null,
) => {
  const file = await fileOpen({
    description: "Drawink files",
    // ToDo: Be over-permissive until https://bugs.webkit.org/show_bug.cgi?id=34442
    // gets resolved. Else, iOS users cannot open `.drawink` files.
    // extensions: ["json", "drawink", "png", "svg"],
  });
  return loadFromBlob(file, localAppState, localElements, file.handle);
};

export const isValidDrawinkData = (data?: {
  type?: any;
  elements?: any;
  appState?: any;
}): data is ImportedDataState => {
  return (
    isDrawinkDataType(data?.type) &&
    (!data.elements ||
      (Array.isArray(data.elements) && (!data.appState || typeof data.appState === "object")))
  );
};

export const isValidLibrary = (json: any): json is ImportedLibraryData => {
  return (
    typeof json === "object" &&
    json &&
    json.type === EXPORT_DATA_TYPES.drawinkLibrary &&
    (json.version === 1 || json.version === 2)
  );
};

export const serializeLibraryAsJSON = (libraryItems: LibraryItems) => {
  const data: ExportedLibraryData = {
    type: EXPORT_DATA_TYPES.drawinkLibrary,
    version: VERSIONS.drawinkLibrary,
    source: getExportSource(),
    libraryItems,
  };
  return JSON.stringify(data, null, 2);
};

export const saveLibraryAsJSON = async (libraryItems: LibraryItems) => {
  const serialized = serializeLibraryAsJSON(libraryItems);
  await fileSave(
    new Blob([serialized], {
      type: MIME_TYPES.drawinklib,
    }),
    {
      name: "library",
      extension: "drawinklib",
      description: "Drawink library file",
    },
  );
};
